import { useEffect, useState } from 'react'

// The flat program fee, typed by hand (WS #1944843). Shared by Add Contact and the regenerate-plan
// modal, so the two cannot disagree about what a valid amount is.
//
// The rule is the server's (services/payments/feeType.js): whole dollars, above $0, at most the cap
// the office config sends as `feeShape.max`. The server enforces it again on every save.
//
// THE CONTRACT: `amount` is a number ONLY when the whole rule holds; every other entry is null. Save
// (isProgramPlanIncomplete), Calculate (feeAmountError) and the saved shape (buildSavedFeeShape) all
// ask just "a number above zero?" — and buildSavedFeeShape saves a PERCENTAGE for anything else — so
// letting "8,500", 8750.5 or 45000 through as a number would re-price or mis-price the client.

const formatCap = (max) => `$${Number(max).toLocaleString('en-US')}`

/**
 * @param {string} text what the rep typed
 * @param {{ max?: number }} options the office's cap; none means no cap on screen
 * @returns {{ amount: number|null, error: string|null }}
 */
export const parseFlatFeeInput = (text, { max } = {}) => {
  const cleaned = String(text ?? '').trim().replace(/^\$\s*/, '').replace(/,/g, '')

  if (cleaned === '') return { amount: null, error: null }

  // Zero cents ("8,500.00") are whole dollars; any other cents are not.
  if (/^-?\d+\.\d*[1-9]\d*$/.test(cleaned)) return { amount: null, error: 'Whole dollars only' }
  if (!/^-?\d+(\.0*)?$/.test(cleaned)) return { amount: null, error: 'Enter a whole-dollar amount' }

  const amount = Number(cleaned)
  if (amount <= 0) return { amount: null, error: 'Must be greater than $0' }
  if (Number.isFinite(max) && amount > max) return { amount: null, error: `At most ${formatCap(max)}` }

  return { amount, error: null }
}

/**
 * The text in the field, kept in step with the number in the plan.
 *
 * Typing hands the plan the parsed amount (or null). When the plan's amount changes and no longer
 * matches what the text parses to, the change came from outside — a plan loaded or reopened, or a
 * switch to Flat that cleared it — and the text follows it. The plan echoing the rep's own entry back
 * always matches, so it never rewrites what they are typing, valid or not.
 *
 * Call it unconditionally, whether or not the field is on screen.
 */
export const useFlatFeeText = ({ amount, max, onAmount }) => {
  const [text, setText] = useState(amount == null ? '' : String(amount))

  useEffect(() => {
    if (parseFlatFeeInput(text, { max }).amount !== (amount ?? null)) {
      setText(amount == null ? '' : String(amount))
    }
    // Only an outside change of the amount re-syncs the text; the text itself is the rep's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount])

  const onChange = (e) => {
    const next = e.target.value
    setText(next)
    onAmount(parseFlatFeeInput(next, { max }).amount)
  }

  return { text, error: parseFlatFeeInput(text, { max }).error, onChange }
}
